import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSeo } from "@/context/SeoContext";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://perthtilingwa.com.au").replace(/\/$/, "");

function localBusinessJsonLd(b) {
  if (!b) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "@id": `${SITE_URL}/#business`,
    name: b.name,
    description: b.tagline,
    url: SITE_URL,
    telephone: b.phone_e164 || b.phone_display,
    email: b.email,
    image: b.default_image,
    logo: `${SITE_URL}${b.logo_url || "/brand/logo.png"}`,
    priceRange: b.price_range || "$$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: b.street || undefined,
      addressLocality: b.suburb || "Perth",
      addressRegion: b.region || "WA",
      postalCode: b.postcode || undefined,
      addressCountry: b.country || "AU",
    },
    areaServed: (b.area_served || []).map((a) => ({ "@type": "City", name: a })),
    sameAs: (b.same_as || []).filter(Boolean),
  };
  if (b.latitude && b.longitude) {
    data.geo = { "@type": "GeoCoordinates", latitude: b.latitude, longitude: b.longitude };
  }
  if (Array.isArray(b.hours) && b.hours.length) {
    data.openingHoursSpecification = b.hours.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    }));
  }
  return data;
}

export default function Seo({ pageKey = "home", service = null, faqs = null, breadcrumb = null, overrideTitle = null, overrideDescription = null, overrideKeywords = null, noindex = false, reviews = null, article = null }) {
  const { seo } = useSeo();
  const location = useLocation();
  const business = seo?.business;
  const page = seo?.pages?.[pageKey] || {};

  const title = overrideTitle || page.title || "Perth Tiling WA";
  const description = overrideDescription || page.description || "";
  const keywords = overrideKeywords || page.keywords || "";
  const canonical = `${SITE_URL}${location.pathname === "/" ? "/" : location.pathname}`;
  const image = business?.default_image || `${SITE_URL}/brand/logo.png`;
  const googleVerify = seo?.verification?.google;

  const jsonLd = [];
  const lb = localBusinessJsonLd(business);
  if (lb) {
    if (reviews && reviews.length) {
      const rated = reviews.filter((r) => r.rating);
      if (rated.length) {
        const avg = (rated.reduce((s, r) => s + r.rating, 0) / rated.length).toFixed(1);
        lb.aggregateRating = { "@type": "AggregateRating", ratingValue: avg, reviewCount: String(rated.length), bestRating: "5", worstRating: "1" };
        lb.review = rated.slice(0, 5).map((r) => ({
          "@type": "Review",
          reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
          author: { "@type": "Person", name: r.author },
          reviewBody: r.text,
        }));
      }
    }
    jsonLd.push(lb);
  }

  if (service && business) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: service.name,
      name: `${service.name} — Perth`,
      description,
      provider: { "@id": `${SITE_URL}/#business` },
      areaServed: { "@type": "AdministrativeArea", name: "Perth, Western Australia" },
      url: canonical,
    });
  }

  if (breadcrumb && breadcrumb.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumb.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    });
  }

  if (faqs && faqs.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (article) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.headline || title,
      description,
      image: image ? [image] : undefined,
      datePublished: article.datePublished || undefined,
      dateModified: article.dateModified || article.datePublished || undefined,
      author: { "@type": "Organization", name: "Perth Tiling WA" },
      publisher: { "@id": `${SITE_URL}/#business` },
      mainEntityOfPage: canonical,
    });
  }

  return (
    <Helmet>
      <html lang="en-AU" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {googleVerify && <meta name="google-site-verification" content={googleVerify} />}
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />
      <meta name="geo.region" content="AU-WA" />
      <meta name="geo.placename" content="Perth" />

      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:site_name" content="Perth Tiling WA" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_AU" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
